import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, Loader2, Lock, TrendingUp } from 'lucide-react';
import { trainingApi } from '../../services/api';
import { useAppStore } from '../../store/useAppStore';
import { CheckoutBadge, NotWorked } from '../workitems/CheckoutBadge';

/**
 * Where the programme's work stands, from its training project's work items (EP-26 FE-17
 * ST-3): progress per epic, what is blocked and by whom, what is being worked, what waits
 * for approval, and what to do next by WSJF. Every row opens the item panel, where it can
 * be moved, assigned and checked out like any project's item.
 */
type D = Record<string, any>;

function Bar({ done, total }: { done: number; total: number }) {
  const pct = total ? Math.round((100 * done) / total) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[10rem]">
      <div className="flex-1 h-2 rounded bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div className="h-2 bg-emerald-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] text-gray-500 w-20 text-right">{done}/{total} · {pct}%</span>
    </div>
  );
}

export default function ProgressPanel({ programId }: { programId: string }) {
  const [d, setD] = useState<D | null>(null);
  const [error, setError] = useState('');
  const [showCopied, setShowCopied] = useState(false);
  const open = useAppStore((s) => s.setDetailItemId);

  const load = useCallback(() => {
    trainingApi.progress(programId).then(setD).catch((e) => setError(String(e?.message || e)));
  }, [programId]);
  useEffect(() => { setD(null); load(); }, [load]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!d) return <p className="text-sm text-gray-500 flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> Reading progress…</p>;
  if (d.state !== 'ok') return <p className="text-sm text-amber-700">{d.message}</p>;

  const row = (it: D, extra?: React.ReactNode) => (
    <button key={it._id} type="button" onClick={() => open(it._id)}
      className="w-full text-left flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 text-sm">
      <span className="font-mono text-[11px] text-gray-400 shrink-0">{it.key || it.displayId}</span>
      <span className="truncate flex-1 text-gray-800 dark:text-gray-200">{it.title}</span>
      <span className="text-[11px] text-gray-500 shrink-0">{it.status}</span>
      {extra}
    </button>
  );
  const epics = (d.epics as D[]).filter((e) => showCopied || !e.copied || !e.finished);
  const t = d.totals;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 grid-cols-2 md:grid-cols-5">
        {[['Work items', `${t.done}/${t.items} done`, CheckCircle2], ['Blocked', t.blocked, AlertTriangle],
          ['Being worked', t.checkedOut, Lock], ['Awaiting approval', t.awaitingApproval, Clock],
          ['Next by WSJF', (d.next as D[]).length, TrendingUp]].map(([label, value, Icon]: any) => (
          <div key={label} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="text-xs text-gray-500 flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {label}</div>
            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{value}</div>
          </div>))}
      </div>

      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-sm font-semibold">Progress by epic</h3>
          <label className="ml-auto text-xs text-gray-500 flex items-center gap-1">
            <input type="checkbox" checked={showCopied} onChange={(e) => setShowCopied(e.target.checked)} /> include finished copied boards</label>
          <Link to={`/training/${encodeURIComponent(programId)}/work/board`} className="text-xs text-accent hover:underline">Board</Link>
          <Link to={`/training/${encodeURIComponent(programId)}/work/gantt`} className="text-xs text-accent hover:underline">Gantt</Link>
        </div>
        <div className="space-y-1">
          {epics.map((e) => (
            <div key={e._id}>
            <div className="flex items-center gap-3 text-sm">
              <button type="button" onClick={() => open(e._id)} className="text-left flex-1 truncate hover:text-accent">
                <span className="font-mono text-[11px] text-gray-400 mr-2">{e.key || e.displayId}</span>{e.title}
                {e.kind ? <span className="ml-2 text-[10px] px-1 rounded bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300">{e.kind}</span> : null}
                {e.copied ? <span className="ml-1 text-[10px] text-gray-400">copied</span> : null}
              </button>
              <span className="text-[11px] text-gray-500 w-24 text-right">{e.pointsDone}/{e.points} SP</span>
              <Bar done={e.done} total={e.items} />
            </div>
            {((e.batches || []) as D[]).filter((b) => !b.finished || showCopied).map((b) => (
              <div key={b._id} className="flex items-center gap-3 text-xs pl-6">
                <button type="button" onClick={() => open(b._id)} className="text-left flex-1 truncate text-gray-600 dark:text-gray-400 hover:text-accent">
                  <span className="font-mono text-[10px] text-gray-400 mr-2">{b.displayId}</span>{b.title}
                  {b.kind ? <span className="ml-2 text-[10px] text-gray-400">{b.kind} · {b.status}</span> : null}
                </button>
                <Bar done={b.done} total={b.items} />
              </div>))}
            </div>))}
          {!epics.length && <p className="text-xs text-gray-500">No epics yet. Create a campaign from the process template.</p>}
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <h3 className="text-sm font-semibold mb-1 flex items-center gap-1"><AlertTriangle className="w-4 h-4 text-amber-600" /> Blocked</h3>
          {!(d.blocked as D[]).length && <p className="text-xs text-gray-500">Nothing is waiting on an open dependency.</p>}
          {(d.blocked as D[]).map((b) => (
            <div key={b._id} className="mb-1">
              {row(b)}
              {(b.blockedBy as D[]).map((x) => (
                <div key={x._id} className="ml-6 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <span>waits on</span>
                  <button type="button" onClick={() => open(x._id)} className="truncate hover:text-accent">{x.key || x.displayId} {x.title}</button>
                  {x.checkout ? <CheckoutBadge checkout={x.checkout} compact /> : <NotWorked />}
                </div>))}
            </div>))}
        </section>
        <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <h3 className="text-sm font-semibold mb-1 flex items-center gap-1"><TrendingUp className="w-4 h-4" /> Next by WSJF</h3>
          {(d.next as D[]).map((n) => row(n, <>
            <span className="text-[11px] font-mono text-gray-500 w-10 text-right">{n.wsjfScore}</span>
            {n.checkout ? <CheckoutBadge checkout={n.checkout} compact /> : null}</>))}
          {!(d.next as D[]).length && <p className="text-xs text-gray-500">No scored features or stories open.</p>}
        </section>
        <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <h3 className="text-sm font-semibold mb-1 flex items-center gap-1"><Lock className="w-4 h-4" /> Being worked</h3>
          {(d.checkedOut as D[]).map((c) => row(c, <CheckoutBadge checkout={c.checkout} compact />))}
          {!(d.checkedOut as D[]).length && <p className="text-xs text-gray-500">Nothing is checked out.</p>}
        </section>
        <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <h3 className="text-sm font-semibold mb-1 flex items-center gap-1"><Clock className="w-4 h-4" /> Awaiting approval
            {d.totals?.awaitingMe ? <span className="ml-1 text-[11px] font-normal text-amber-700">{d.totals.awaitingMe} waiting for you</span> : null}</h3>
          {(d.approvals as D[]).map((a) => row(a, <span className={`text-[11px] ${a.forMe ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>
            {a.forMe ? 'yours to decide · ' : ''}{a.pendingApproval?.approvals}/{a.pendingApproval?.required} · {a.pendingApproval?.permission}</span>))}
          {!(d.approvals as D[]).length && <p className="text-xs text-gray-500">No approvals waiting.</p>}
        </section>
      </div>
    </div>
  );
}
